import React, { useState } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  message, 
  Typography, 
  Space,
  Modal,
  Alert
} from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  LockOutlined, 
  EditOutlined,
  SaveOutlined,
  KeyOutlined,
  HomeOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useLoading } from '../../hooks/useLoading';
import { validationRules } from '../../utils/formUtils';
import { showError } from '../common/ErrorMessage';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;

const Profile: React.FC = () => {
  const { user, updateProfile, updatePassword } = useAuth();
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const { loading, withLoading } = useLoading(false);
  const { loading: passwordLoading, withLoading: withPasswordLoading } = useLoading(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();



  // 프로필 폼 초기값 설정
  React.useEffect(() => {
    if (user) {
      profileForm.setFieldsValue({
        username: user.username,
        email: user.email,
        organ_name: user.organ_name || '',
        bubmyeng: user.bubmyeng || '',
      });
    }
  }, [user, profileForm]);

  const handleProfileUpdate = async (values: any) => {
    await withLoading(async () => {
      try {
        await updateProfile({
          email: values.email,
          organ_name: values.organ_name || undefined,
          bubmyeng: values.bubmyeng || undefined,
        });
        message.success(t('profile.updateSuccess'));
      } catch (error: any) {
        showError(error, t('profile.updateError'));
      }
    });
  };

  const handlePasswordUpdate = async (values: any) => {
    await withPasswordLoading(async () => {
      try {
        await updatePassword({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        });
        message.success(t('profile.passwordChangeSuccess'));
        setShowPasswordModal(false);
        passwordForm.resetFields();
      } catch (error: any) {
        showError(error, t('profile.passwordChangeError'));
      }
    });
  };

  if (!user) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Alert
          message={t('profile.loginRequired')}
          description={t('profile.loginRequiredDesc')}
          type="info"
          showIcon
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0 }}>
          {t('profile.title') || '프로필 관리'}
        </Title>
        <Button 
          type="default" 
          icon={<HomeOutlined />}
          onClick={() => navigate('/')}
        >
          {t('profile.backToMain')}
        </Button>
      </div>

      <Card style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <Title level={4} style={{ margin: 0 }}>
            {t('profile.basicInfo') || '기본 정보'}
          </Title>
          <Button 
            type="primary" 
            icon={<EditOutlined />}
            onClick={() => profileForm.submit()}
            loading={loading}
          >
            {t('profile.save')}
          </Button>
        </div>

        <Form
          form={profileForm}
          layout="vertical"
          onFinish={handleProfileUpdate}
        >
          <Form.Item
            name="username"
            label={t('auth.username')}
          >
            <Input 
              prefix={<UserOutlined />} 
              disabled 
              placeholder={t('auth.username')}
            />
          </Form.Item>

          <Form.Item
            name="email"
            label={t('auth.email')}
            rules={validationRules.email}
          >
            <Input 
              prefix={<MailOutlined />} 
              placeholder={t('auth.email')}
            />
          </Form.Item>

          <Form.Item
            name="bubmyeng"
            label={t('auth.bubmyeng')}
            rules={validationRules.bubmyeng}
          >
            <Input placeholder={t('profile.bubmyengOptional')} />
          </Form.Item>

          <Form.Item
            name="organ_name"
            label={t('auth.organName')}
            rules={validationRules.organ_name}
          >
            <Input placeholder={t('profile.organNameOptional')} />
          </Form.Item>
        </Form>
      </Card>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4} style={{ margin: 0 }}>
            {t('profile.securitySettings') || '보안 설정'}
          </Title>
          <Button 
            type="default" 
            icon={<KeyOutlined />}
            onClick={() => setShowPasswordModal(true)}
          >
            {t('profile.changePassword')}
          </Button>
        </div>
        
        <div style={{ marginTop: '16px' }}>
          <Alert
            message={t('profile.passwordSecurity') || '비밀번호 보안'}
            description={t('profile.passwordSecurityDesc') || '정기적으로 비밀번호를 변경하여 계정을 보호하세요.'}
            type="info"
            showIcon
          />
        </div>
      </Card>

      {/* 비밀번호 변경 모달 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <KeyOutlined style={{ marginRight: '8px' }} />
            {t('profile.changePassword')}
          </div>
        }
        open={showPasswordModal}
        onOk={() => passwordForm.submit()}
        onCancel={() => {
          setShowPasswordModal(false);
          passwordForm.resetFields();
        }}
        okText={t('profile.change')}
        cancelText={t('profile.cancel')}
        width={500}
        centered
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordUpdate}
        >
          <Form.Item
            name="currentPassword"
            label={t('profile.currentPassword')}
            rules={[
              { required: true, message: t('validation.required', { field: t('profile.currentPassword') }) }
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder={t('profile.currentPassword')}
            />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label={t('profile.newPassword')}
            rules={validationRules.password}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder={t('profile.newPassword')}
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label={t('profile.confirmPassword')}
            dependencies={['newPassword']}
            rules={validationRules.confirmPassword('newPassword')}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder={t('profile.confirmPassword')}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button 
                onClick={() => {
                  setShowPasswordModal(false);
                  passwordForm.resetFields();
                }}
              >
                {t('profile.cancel')}
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={passwordLoading}
                icon={<SaveOutlined />}
              >
                {t('profile.change')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Profile; 